import React, { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { Modal, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons";
import { toast } from "react-toastify";
import {
  bindGoogleDrive,
  openGoogleDriveOAuthPopup,
} from "../../../../cores/googleDrive/connectApi";
import "./GoogleDriveConnectModal.scss";

type Props = {
  isOpen: boolean;
  toggle: () => void;
  companyId: string;
  onConnected?: () => void;
};

export const GoogleDriveConnectModal = ({
  isOpen,
  toggle,
  companyId,
  onConnected,
}: Props) => {
  const intl = useIntl();
  const [busy, setBusy] = useState(false);

  const handleConnect = useCallback(async () => {
    setBusy(true);
    try {
      const code = await openGoogleDriveOAuthPopup(companyId);
      await bindGoogleDrive(companyId, code);
      toast.success(intl.formatMessage({ id: "app.googleDrive.connected" }));
      toggle();
      onConnected?.();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          e?.message ||
          intl.formatMessage({ id: "app.googleDrive.connectError" })
      );
    } finally {
      setBusy(false);
    }
  }, [companyId, intl, onConnected, toggle]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="google-drive-connect-modal-dialog"
      modalClassName="google-drive-connect-modal"
      centered
      backdrop="static"
      zIndex={2000}
    >
      <div className="google-drive-connect-modal__header">
        <div className="google-drive-connect-modal__header-brand">
          <FontAwesomeIcon icon={faGoogleDrive} size="lg" color="#4285F4" />
          <span>Google Drive</span>
        </div>
        <button type="button" className="google-drive-connect-modal__close" onClick={toggle}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <ModalBody className="google-drive-connect-modal__body">
        <div className="google-drive-connect-modal__hero">
          <FontAwesomeIcon icon={faGoogleDrive} size="4x" color="#4285F4" />
        </div>
        <h2>{intl.formatMessage({ id: "app.googleDrive.connectTitle" })}</h2>
        <p>{intl.formatMessage({ id: "app.googleDrive.connectDescription" })}</p>
        <p className="text-muted small">
          {intl.formatMessage({ id: "app.googleDrive.connectPrivacy" })}
        </p>

        <Button
          color="light"
          className="google-drive-connect-modal__btn-connect"
          onClick={handleConnect}
          disabled={busy}
        >
          {busy ? (
            <Spinner size="sm" />
          ) : (
            <>
              <FontAwesomeIcon icon={faGoogleDrive} className="mr-2" color="#4285F4" />
              {intl.formatMessage({ id: "app.googleDrive.connect" })}
            </>
          )}
        </Button>

        <p className="google-drive-connect-modal__footnote text-muted small">
          {intl.formatMessage({ id: "app.googleDrive.connectDisclaimer" })}
        </p>
      </ModalBody>

      <ModalFooter>
        <Button color="primary" onClick={toggle}>
          {intl.formatMessage({ id: "app.googleDrive.accept" })}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
